import type { TaskLite } from '@/gen/cortado/v1/actions_pb'
import type { TaskStatusEnum, ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import type { KanbanItem } from '@/components/shared/kanban'

/**
 * TaskKanbanItem extends KanbanItem for use with the KanbanBoard component.
 * Maps TaskLite fields to a kanban-compatible structure.
 */
export interface TaskKanbanItem extends KanbanItem<TaskStatusEnum> {
  id: string
  status: TaskStatusEnum // Mapped from taskStatus for KanbanBoard compatibility
  title: string
  description: string
  dueDate?: Timestamp
  assignedTo: string
  assignedToFirstName: string
  assignedToLastName: string
  parentType: ParentTypeEnum
  parentId: string
  parentName?: string
  createdAt?: Timestamp
  updatedAt?: Timestamp
}

/**
 * Maps a TaskLite proto message to a TaskKanbanItem for the kanban board.
 *
 * This function:
 * - Converts proto TaskLite to UI-friendly TaskKanbanItem type
 * - Maps taskStatus to status for KanbanBoard compatibility
 * - Preserves all fields needed for display and editing
 *
 * @param task - Proto TaskLite message from backend
 * @returns TaskKanbanItem ready for kanban board consumption
 */
export function mapTaskLiteToKanbanItem(task: TaskLite): TaskKanbanItem {
  return {
    id: task.id,
    status: task.taskStatus, // Map taskStatus to status for KanbanItem
    title: task.title,
    description: task.description,
    dueDate: task.dueDate,
    assignedTo: task.assignedTo,
    assignedToFirstName: task.assignedToFirstName,
    assignedToLastName: task.assignedToLastName,
    parentType: task.parentType,
    parentId: task.parentId,
    parentName: task.parentName,
    createdAt: task.createdAt,
    updatedAt: task.updatedAt,
  }
}
